import { useCallback, useEffect, useRef, useState } from "react";
import { readGameError, useLazyGetGameReachQuery } from "../api/gameApi";
import type { GameReach } from "../api/types";

export const REACH_POLL_MS = 5000;
export const REACH_POLL_FOR_MS = 3 * 60 * 1000;

function pageVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export interface GameReachState {
  reach: GameReach | null;
  /** Plain message when the check failed (messenger down, signed out). */
  errorMessage: string | null;
  checking: boolean;
  /** Ask the server for a fresh answer (bypasses its 15 s cache) and keep polling. */
  recheck: () => void;
  /** Keep polling for another few minutes, e.g. after opening Telegram. */
  watch: () => void;
}

/**
 * The caller's reach for the game (GET /game/reach): polled every 5 s while
 * `active` and the tab is visible, for 3 minutes at a time, stopping once
 * reachable. Coming back to the tab (from Telegram, from the phone's settings)
 * asks for a fresh answer straight away.
 */
export function useGameReach(active: boolean): GameReachState {
  const [trigger, result] = useLazyGetGameReachQuery();
  const [pollUntil, setPollUntil] = useState(0);
  const [visible, setVisible] = useState(pageVisible);
  const activeRef = useRef(active);
  activeRef.current = active;

  const check = useCallback(
    (fresh: boolean) => {
      void trigger(fresh ? { fresh: true } : undefined);
    },
    [trigger],
  );

  const watch = useCallback(() => setPollUntil(Date.now() + REACH_POLL_FOR_MS), []);

  const recheck = useCallback(() => {
    check(true);
    watch();
  }, [check, watch]);

  // first look when the step opens
  useEffect(() => {
    if (!active) return;
    check(false);
    watch();
  }, [active, check, watch]);

  // back from Telegram or the settings app: check fresh right away
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibility = () => {
      const v = pageVisible();
      setVisible(v);
      if (v && activeRef.current) {
        check(true);
        setPollUntil(Date.now() + REACH_POLL_FOR_MS);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [check]);

  const reachable = result.data?.reachable === true;

  useEffect(() => {
    if (reachable) setPollUntil(0);
  }, [reachable]);

  useEffect(() => {
    if (!active || !visible || pollUntil === 0) return undefined;
    const t = setInterval(() => {
      if (Date.now() > pollUntil) {
        setPollUntil(0);
        return;
      }
      check(false);
    }, REACH_POLL_MS);
    return () => clearInterval(t);
  }, [active, visible, pollUntil, check]);

  let errorMessage: string | null = null;
  if (result.isError) {
    const e = readGameError(result.error);
    errorMessage =
      e.message ||
      (e.code === "auth_required"
        ? "Sign in to check your notifications."
        : "We could not check your notifications right now. Try again in a minute.");
  }

  return {
    reach: result.data ?? null,
    errorMessage,
    checking: result.isFetching,
    recheck,
    watch,
  };
}
